package com.example.mystybag.appCore.helpers

import com.example.mystybag.communication.Communication
import com.example.mystybag.communication.common.CommonCommands.sendUpdatePushToken
import com.example.mystybag.communication.ext.CommandResult
import com.example.mystybag.core.defines.UtilsDefines.PUSH_PROVIDER_NONE
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_PUSH_TOKEN
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.notificationsUtils.expected.PushProviderHelper
import com.example.mystybag.remoteLogger.RemoteLogger
import com.example.mystybag.sharedmodels.common.requestModels.UpdatePushTokenRequestModel
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

/**
 * Отделна логика за push токен към сървъра: [sendUpdatePushToken] → `user/updatePushToken`.
 * На бекенда същият endpoint обработва клиент (userId), служител (staffId) и собственик (merchantId) от JWT.
 *
 * При FCM [onNewToken] извиквай [scheduleSendPushTokenToServer] (фонов scope).
 * От UI / ViewModel — [syncCurrentDevicePushToken] в съществуващ [commandBlock].
 */
object PushTokenServerSync {

    private const val DEFAULT_AWAIT_TOKEN_TIMEOUT_MS = 15_000L

    private val backgroundScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    /**
     * Emit-ва се при пристигане на нов push токен от платформата
     * (Android: FCM onNewToken, iOS: didRegisterForRemoteNotifications).
     *
     * Стартова стойност `null` означава "още не е известен в текущия процес".
     * StateFlow replay-ва последната стойност, така че абонат който се закачи
     * след emit-а пак ще я получи моментално.
     */
    private val _devicePushToken = MutableStateFlow<String?>(null)
    val devicePushToken: StateFlow<String?> get() = _devicePushToken.asStateFlow()

    /**
     * Извиква се от платформените интеграции при получаване на нов push токен.
     * След това абонатите на [devicePushToken] / [awaitDevicePushTokenAndSync] могат да реагират.
     */
    fun notifyDevicePushTokenReceived(token: String) {
        if (token.isEmpty()) return
        _devicePushToken.value = token
    }

    /**
     * Изпраща вече известен токен и provider (напр. нов токен от Firebase преди да е кеширан).
     */
    suspend fun sendPushTokenToServer(token: String, pushProvider: Int): Boolean {
        if (token.isEmpty() || pushProvider == PUSH_PROVIDER_NONE) {
            RemoteLogger.log("PushTokenServerSync: skip (empty token or NONE)", RemoteLogger.TYPE_CUSTOM)
            return false
        }
        if (Communication.token.isEmpty()) {
            RemoteLogger.log("PushTokenServerSync: skip (JWT empty)", RemoteLogger.TYPE_CUSTOM)
            return false
        }

        return when (val result = sendUpdatePushToken(UpdatePushTokenRequestModel(token = token))) {
            is CommandResult.Success -> {
                Preferences.setString(PREFERENCES_PUSH_TOKEN, token)
                RemoteLogger.log("PushTokenServerSync: user/updatePushToken OK", RemoteLogger.TYPE_CUSTOM)
                true
            }
            is CommandResult.BaseError -> {
                RemoteLogger.log(
                    "PushTokenServerSync: user/updatePushToken failed status=${result.baseErrorModel.status} msg=${result.baseErrorModel.statusMessage}",
                    RemoteLogger.TYPE_CUSTOM,
                )
                false
            }
            is CommandResult.Error -> {
                RemoteLogger.log("PushTokenServerSync: user/updatePushToken error branch", RemoteLogger.TYPE_CUSTOM)
                false
            }
        }
    }

    /**
     * Взима токен от платформата (preferences / FCM) и го изпраща към сървъра.
     */
    suspend fun syncCurrentDevicePushToken(): Boolean {
        val (token, pushProvider) = PushProviderHelper.getToken()
        return sendPushTokenToServer(token, pushProvider)
    }

    /**
     * Изчаква push токенът да стане наличен (FCM onNewToken / iOS didRegister) и го синхронизира към сървъра.
     *
     * Поведение:
     * 1. Опитва веднага да вземе кеширан токен от платформата ([PushProviderHelper.getToken]). Ако има — праща го.
     * 2. Иначе се абонира за [devicePushToken] и чака първия не‑празен emit (с [timeoutMs] timeout).
     * 3. При timeout логва и връща false. Реалната доставка ще се случи когато
     *    [notifyDevicePushTokenReceived] / [scheduleSendPushTokenToServer] се извика от платформените callback-и.
     *
     * Това замества fixed `delay(...)`-те — реагираме на събитието, а не познаваме време.
     */
    suspend fun awaitDevicePushTokenAndSync(
        timeoutMs: Long = DEFAULT_AWAIT_TOKEN_TIMEOUT_MS,
    ): Boolean {
        val (cachedToken, cachedProvider) = PushProviderHelper.getToken()
        if (cachedToken.isNotEmpty() && cachedProvider != PUSH_PROVIDER_NONE) {
            return sendPushTokenToServer(cachedToken, cachedProvider)
        }

        val received = withTimeoutOrNull(timeoutMs) {
            _devicePushToken.filterNotNull().first { it.isNotEmpty() }
        }
        if (received.isNullOrEmpty()) {
            RemoteLogger.log(
                "PushTokenServerSync: awaitDevicePushTokenAndSync timeout after ${timeoutMs}ms",
                RemoteLogger.TYPE_CUSTOM,
            )
            return false
        }
        return sendPushTokenToServer(received, PushProviderHelper.getPushProvider())
    }

    /**
     * Изпраща токена веднага и при нужда повторно (FCM може да дойде преди JWT, или обратното).
     */
    fun scheduleSendPushTokenToServer(token: String, pushProvider: Int) {
        backgroundScope.launch {
            val delaysMs = listOf(0L, 900L, 2800L, 8000L)
            for (d in delaysMs) {
                if (d > 0) delay(d)
                if (sendPushTokenToServer(token, pushProvider)) break
            }
        }
    }
}
